/**
 * MKS-339 — triage a request without leaving the inbox.
 *
 * Owner, tier and category are three selects; "Move to Open" is the existing
 * Phase 51 transition. The button is deliberately NOT disabled when the two
 * prerequisites are missing — the backend refuses with
 * `task_intake_assignee_required` / `task_tier_required` and the refusal is
 * surfaced as a toast, which tells the user WHICH one is missing. A disabled
 * button says only "no", and on a project with no SLA tiers at all (where tier
 * is not required) it would say "no" wrongly.
 *
 * Each select fires its own mutation on change: triage is three independent
 * decisions, and making the user press Save after each one — or losing two of
 * them when the third fails — is worse than three round trips.
 */
import { useState } from 'react';
import { Btn } from '../ui/Btn';
import { DropdownSelect } from '../ui/DropdownSelect';
import { useToast } from '../ui/Toast';
import { useProjectAssignments } from '../../hooks/useProjects';
import { useProjectSlaTiers } from '../../hooks/useProjectSlaTiers';
import {
  useRequestCategories,
  useSetTaskCategory,
  useSetRequestOwner,
  useSetRequestTier,
  useMoveRequestToOpen,
} from '../../hooks/useHelpdesk';
import type { HelpdeskRequest } from '../../services/api/helpdesk';

export interface TriagePanelProps {
  request: HelpdeskRequest;
}

function errorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  if (code === 'task_intake_assignee_required') {
    return 'Pick an owner before moving this request to Open.';
  }
  if (code === 'task_tier_required') {
    return 'Pick a priority tier before moving this request to Open.';
  }
  return (err as Error)?.message ?? 'Could not save. Try again.';
}

export function TriagePanel({ request }: TriagePanelProps) {
  const showToast = useToast();
  const [pending, setPending] = useState<string | null>(null);

  const { data: assignments = [] } = useProjectAssignments(request.projectId);
  const { data: tiersResponse } = useProjectSlaTiers(request.projectId);
  const { data: categories = [] } = useRequestCategories(request.projectId);

  const tiers = tiersResponse?.data ?? [];

  const setOwner = useSetRequestOwner();
  const setTier = useSetRequestTier();
  const setCategory = useSetTaskCategory();
  const moveToOpen = useMoveRequestToOpen();

  function run(label: string, promise: Promise<unknown>, successMessage: string) {
    setPending(label);
    promise
      .then(() => showToast(successMessage, 'success'))
      .catch((err) => showToast(errorMessage(err), 'error'))
      .finally(() => setPending(null));
  }

  return (
    <div
      className="flex flex-wrap items-end gap-3 px-4 py-3 bg-ms-surface-2 border-t border-ms-border"
      data-testid={`triage-panel-${request.taskId}`}
    >
      <label className="flex flex-col gap-1">
        <span className="text-[10.5px] uppercase tracking-wider text-ms-text-muted">Owner</span>
        <DropdownSelect
          aria-label="Owner"
          value={request.ownerId ?? ''}
          disabled={pending !== null}
          onChange={(e) =>
            run(
              'owner',
              setOwner.mutateAsync({
                taskId: request.taskId,
                ownerId: e.target.value === '' ? null : Number(e.target.value),
              }),
              'Owner updated',
            )
          }
        >
          <option value="">Unassigned</option>
          {assignments.map((a) => (
            <option key={a.userId} value={a.userId}>
              {/* The nested user is typed nullable on the assignment DTO
                  (legacy rows with a dangling user_id), so fall back to the
                  id rather than rendering an empty option nobody can tell
                  apart from the next one. */}
              {a.user?.name ?? `User ${a.userId}`}
            </option>
          ))}
        </DropdownSelect>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[10.5px] uppercase tracking-wider text-ms-text-muted">Tier</span>
        <DropdownSelect
          aria-label="Tier"
          value={request.tierId ?? ''}
          // A project with no SLA tiers has nothing to pick, and the backend
          // does not require a tier there either.
          disabled={pending !== null || tiers.length === 0}
          onChange={(e) =>
            run(
              'tier',
              setTier.mutateAsync({
                taskId: request.taskId,
                tierId: Number(e.target.value),
              }),
              'Tier updated',
            )
          }
        >
          <option value="" disabled>
            {tiers.length === 0 ? 'No tiers on this project' : 'Pick a tier'}
          </option>
          {tiers.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </DropdownSelect>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[10.5px] uppercase tracking-wider text-ms-text-muted">
          Category
        </span>
        <DropdownSelect
          aria-label="Category"
          value={request.categoryId ?? ''}
          disabled={pending !== null}
          onChange={(e) =>
            run(
              'category',
              setCategory.mutateAsync({
                taskId: request.taskId,
                categoryId: e.target.value === '' ? null : Number(e.target.value),
              }),
              'Category updated',
            )
          }
        >
          <option value="">No category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </DropdownSelect>
      </label>

      {request.status === 'triage' && (
        <Btn
          variant="accent"
          size="md"
          disabled={pending !== null}
          onClick={() =>
            run('move', moveToOpen.mutateAsync(request.taskId), 'Moved to Open')
          }
        >
          Move to Open
        </Btn>
      )}
    </div>
  );
}
